import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Tree, Select, TreeSelect, MultiSelect, Empty } from '@maxtropy/components';
import StatusButton from '@/pages/EnergyAnalysisRanking/components/EnergyTree/StatusButton';
import styles from './index.module.scss';
import { DataNode } from 'antd/es/tree';
import {
  apiV2StaffPageV2Post,
  apiV2DeviceOrganizationWithCodePost,
  apiV2EnergyConsumptionPlanAddOrgMappingPost,
  apiV2EnergyConsumptionPlanGetOrgMappingDetailPost,
  V2EnergyConsumptionPlanGetOrgMappingDetailPostResponse,
  apiV2EnergyConsumptionPlanUpdateOrgMappingPost,
  V2DeviceOrganizationWithCodePostResponse,
  V2EnergyConsumptionPlanGetAreaListPostResponse,
  apiV2EnergyConsumptionPlanGetAreaMapListPost,
} from '@maxtropy/device-customer-apis-v2';
import { Spin } from 'antd';

interface CollocatedModalProps {
  onClose: () => void;
  updateFn: () => void;
  visible: boolean;
  isEdit: boolean;
  room?: number | undefined;
}
interface OptItem {
  label?: string;
  value?: number;
}

type NewDataNode = DataNode & {
  originalChildren?: OrganizationTreeProps[];
};
type FormaTreeNode = DataNode & {
  title?: React.ReactNode;
  value?: string;
  children?: FormaTreeNode[];
};

type OrganizationTreeProps = Exclude<V2DeviceOrganizationWithCodePostResponse, undefined>;

function formatTreeData(data: OrganizationTreeProps[]) {
  return data.map(item => {
    const res: DataNode = {
      ...item,
      key: item.data?.mcid!,
      title: item.data?.name,
      children: formatTreeData(item.children ?? []),
    };
    return res;
  });
}

const findOrgNameByOrgId = (treeData: OrganizationTreeProps[], orgId: string): string | undefined => {
  for (const node of treeData) {
    if (node.data?.mcid === orgId) {
      return node.data.name;
    }
    if (node.children && node.children.length > 0) {
      const name = findOrgNameByOrgId(node.children, orgId);
      if (name) {
        return name;
      }
    }
  }
  return undefined;
};

function selectTreeData(data: OrganizationTreeProps[]) {
  return data.map(item => {
    const res: FormaTreeNode = {
      key: item.data?.mcid!,
      value: item.data?.mcid!,
      title: item.data?.name,
      children: selectTreeData(item.children ?? []),
    };
    return res;
  });
}

function flattenTreeData(data: OrganizationTreeProps[]): NewDataNode[] {
  const result: NewDataNode[] = [];

  const traverse = (nodes: OrganizationTreeProps[]) => {
    nodes.forEach(node => {
      const formattedNode: NewDataNode = {
        ...node,
        key: node.data?.mcid!,
        title: node.data?.name,
        disabled: !node.data?.hasPermission,
        children: [],
        originalChildren: node.children && node.children.length > 0 ? node.children : undefined,
      };
      result.push(formattedNode);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  };

  traverse(data);
  return result;
}
type FetailsProps = Exclude<V2EnergyConsumptionPlanGetOrgMappingDetailPostResponse, undefined>;

const CollocatedModal: FC<CollocatedModalProps> = ({ room, onClose, updateFn, visible, isEdit }) => {
  const [form] = Form.useForm();
  const [areaOp, setAreaOp] = useState<V2EnergyConsumptionPlanGetAreaListPostResponse['list']>([]);
  const [treeData, setTreeData] = useState<OrganizationTreeProps[]>([]);
  const [personnel, setPersonnel] = useState<OptItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [detailsData, setFetailsList] = useState<V2EnergyConsumptionPlanGetOrgMappingDetailPostResponse>({});
  const [dataList, setDataList] = useState<FormaTreeNode[]>([]);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [flattenedData, setFlattenedData] = useState<DataNode[]>([]);
  const [ownerOrgName, setOwnerOrgName] = useState<React.ReactNode>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    apiV2EnergyConsumptionPlanGetAreaMapListPost({}).then(res => {
      setAreaOp(res.list);
    });
    // 组织
    apiV2DeviceOrganizationWithCodePost({}).then(data => {
      if (data) {
        setTreeData([data]);
        setDataList(selectTreeData([data]));
        setFlattenedData(flattenTreeData([data]));
      }
    });

    apiV2StaffPageV2Post({ page: 1, size: 500 }).then(res => {
      if (!res.list) return;
      const result = res.list.map(i => ({ label: i?.customerUserName, value: i?.id }));
      setPersonnel(result);
    });
  }, []);

  useEffect(() => {
    if (!room) return;
    setLoading(true);
    apiV2EnergyConsumptionPlanGetOrgMappingDetailPost({ id: room })
      .then(res => {
        setFetailsList(res);
        const orgList = (res.orgList ?? [])?.map((o: any) => o.orgId);
        const areaIdList = res.areaList?.map(a => a.id);
        const ccUserIdList = res.ccUserList?.map(c => c.userId);
        setCheckedList(orgList);

        form.setFieldsValue({
          orgList,
          areaIdList,
          ccUserIdList,
          firstApproveUserIds: res?.firstApproveUsers?.map(i => i.userId),
          secondApproveUserIds: res?.secondApproveUsers?.map(i => i.userId),
        });
      })
      .finally(() => setLoading(false));
  }, [room]);

  // 弹框确认
  const onOk = () => {
    form.validateFields().then(values => {
      console.log(values.ownerOrgId);
      const orgListWithNames = checkedList.map(orgId => {
        // 从treeData中查找对应的组织名称
        const orgName = findOrgNameByOrgId(treeData, orgId);
        return {
          orgId,
          orgName,
        };
      });
      if (room) {
        apiV2EnergyConsumptionPlanUpdateOrgMappingPost({
          ...values,
          orgList: orgListWithNames,
          id: detailsData?.id,
        }).then(() => {
          updateFn();
          onClose();
        });
      } else {
        // 新增
        apiV2EnergyConsumptionPlanAddOrgMappingPost({ ...values, orgList: orgListWithNames, ownerOrgName }).then(() => {
          updateFn();
          onClose();
        });
      }
    });
  };

  const list = useMemo(() => {
    if (isSearching) {
      return flattenedData.filter(item => (item.title as string)?.toLowerCase().includes(searchValue.toLowerCase()));
    }
    return formatTreeData(treeData);
  }, [treeData, searchValue]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setIsSearching(value.trim() !== '');
  };

  const convertDataToEntities = (dataNodes: DataNode[]) => {
    const keyEntities: Record<string, string[]> = {};
    traverseDataNodes(dataNodes, (item, childKeys) => {
      const { key } = item;
      keyEntities[key] = childKeys;
    });
    return keyEntities;
  };
  const getOriginalKeyEntities = useMemo(() => {
    if (!treeData || treeData.length === 0) return {};

    const keyEntities: Record<string, string[]> = {};
    const processNode = (node: OrganizationTreeProps) => {
      const key = node.data?.mcid!;
      const childKeys: string[] = [];

      const collectChildKeys = (children: OrganizationTreeProps[]) => {
        children.forEach(child => {
          const childKey = child.data?.mcid!;
          childKeys.push(childKey);
          if (child.children && child.children.length > 0) {
            collectChildKeys(child.children);
          }
        });
      };

      if (node.children && node.children.length > 0) {
        collectChildKeys(node.children);
      }

      keyEntities[key] = childKeys;

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => processNode(child));
      }
    };

    treeData.forEach(node => processNode(node));
    return keyEntities;
  }, [treeData]);

  const dataKeyEntities: Record<string, string[]> = useMemo(() => {
    if (isSearching) {
      // 在搜索模式下使用原始树结构的实体映射
      return getOriginalKeyEntities;
    }
    return Array.isArray(list) && list.length > 0 ? convertDataToEntities(list) : {};
  }, [list]);

  const selectAllChild = (key: string, value: boolean) => {
    // 在平铺模式下，需要找到原始节点来获取所有子节点
    if (isSearching) {
      // 从原始树数据中找到对应的节点
      const findNode = (nodes: OrganizationTreeProps[]): OrganizationTreeProps | null => {
        for (const node of nodes) {
          if (node.data!.mcid === key) {
            return node;
          }
          if (node.children && node.children.length > 0) {
            const found = findNode(node.children);
            if (found) return found;
          }
        }
        return null;
      };

      const node = findNode(treeData);
      if (node && node.children) {
        // 获取所有子节点的key
        const getChildKeys = (children: OrganizationTreeProps[]): string[] => {
          let keys: string[] = [];
          children.forEach(child => {
            keys.push(child.data?.mcid!);
            if (child.children && child.children.length > 0) {
              keys = keys.concat(getChildKeys(child.children));
            }
          });
          return keys;
        };

        const allChildKeys = getChildKeys(node.children);

        if (value) {
          // 选中所有子节点
          setCheckedList(Array.from(new Set([...checkedList, ...allChildKeys])));
        } else {
          // 取消选中所有子节点
          setCheckedList(Array.from(new Set(checkedList.filter(item => !allChildKeys.includes(item)))));
        }
      }
    } else {
      // 正常树形模式下的逻辑
      if (value) {
        setCheckedList(Array.from(new Set([...checkedList, ...dataKeyEntities[key]])));
      } else {
        setCheckedList(Array.from(new Set(checkedList.filter(item => !dataKeyEntities[key].includes(item)))));
      }
    }
  };

  const titleRender = (node: NewDataNode) => {
    const hasChildren = isSearching
      ? node.originalChildren && node.originalChildren.length > 0
      : Array.isArray(node.children) && node.children.length > 0;

    const visible = !node.disabled && hasChildren;
    // 修改checked的计算方式，使其在平铺模式下也能正确工作
    let checked = false;
    if (dataKeyEntities[node.key]) {
      // 如果在平铺模式下，需要通过原始树结构来获取子节点
      if (isSearching && node.originalChildren) {
        // 获取原始子节点的key列表
        const getChildKeys = (children: OrganizationTreeProps[]): string[] => {
          let keys: string[] = [];
          children.forEach(child => {
            keys.push(child.data?.mcid!);
            if (child.children && child.children.length > 0) {
              keys = keys.concat(getChildKeys(child.children));
            }
          });
          return keys;
        };

        const allChildKeys = getChildKeys(node.originalChildren);
        // 检查所有子节点是否都被选中
        checked = allChildKeys.length > 0 && allChildKeys.every(key => checkedList.includes(key));
      } else {
        // 正常树形模式下的检查逻辑
        checked = dataKeyEntities[node.key].every(value => checkedList.includes(value));
      }
    }
    return (
      <div>
        <span>{node?.title as React.ReactNode}</span>
        {visible && (
          <StatusButton
            style={{ margin: '2px 5px' }}
            checked={checked}
            onChange={(event, value) => selectAllChild(node.key as string, value)}
          >
            全选子组织
          </StatusButton>
        )}
      </div>
    );
  };
  console.log(isEdit, isEdit);

  return (
    <Modal
      title={`${room ? '编辑' : '配置映射关系'}`}
      open={visible}
      onOk={onOk}
      contentClassName="modal-form-content"
      onCancel={onClose}
    >
      <Spin spinning={loading}>
        <Form form={form}>
          {!room && (
            <Form.Item label="部门" rules={[{ required: true }]} name="ownerOrgId">
              <TreeSelect
                style={{ width: '100%' }}
                treeData={dataList}
                allowClear
                treeDefaultExpandAll
                showSearch
                treeNodeFilterProp="title"
                placeholder="请选择"
                disabled={isEdit}
                onChange={(value, label) => {
                  setOwnerOrgName(label[0] ?? '');
                }}
              />
            </Form.Item>
          )}

          <Form.Item
            label="申请人组织"
            name="orgList"
            rules={[
              { required: true },
              {
                validator: (_, value) => {
                  if (checkedList?.length < 0) {
                    return Promise.reject(new Error());
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Search allowClear placeholder="请选择组织" style={{ flex: 1 }} onSearch={v => handleSearch(v)} />
            <div className={styles.tree_box}>
              {list && list.length > 0 ? (
                <Tree
                  disabled={isEdit}
                  checkStrictly
                  checkable
                  checkedKeys={checkedList}
                  treeData={list}
                  defaultExpandAll
                  onCheck={(checkedKeys: any) => {
                    setCheckedList(checkedKeys.checked);
                    form.setFieldsValue({
                      orgList: checkedKeys.checked,
                    });
                  }}
                  titleRender={titleRender}
                />
              ) : (
                <Empty style={{ marginTop: '10%' }} />
              )}
            </div>
          </Form.Item>
          <Form.Item label="区域" name="areaIdList" rules={[{ required: true }]}>
            <MultiSelect
              style={{ width: '100%' }}
              placeholder="请选择"
              showSearch
              optionFilterProp="label"
              disabled={isEdit}
              options={areaOp?.map(a => ({ label: a.zoneName, value: a.id }))}
            ></MultiSelect>
          </Form.Item>

          <Form.Item label="一级审批人" name="firstApproveUserIds" rules={[{ required: true }]}>
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              showSearch
              mode="multiple"
              optionFilterProp="label"
              disabled={isEdit}
              options={personnel}
            />
          </Form.Item>
          <Form.Item label="二级审批人" name="secondApproveUserIds" rules={[{ required: true }]}>
            <Select
              placeholder="请选择"
              style={{ width: '100%' }}
              showSearch
              mode="multiple"
              disabled={isEdit}
              optionFilterProp="label"
              options={personnel}
            />
          </Form.Item>

          <Form.Item label="抄送人" name="ccUserIdList" rules={[{ required: true }]}>
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              mode="multiple"
              showSearch
              optionFilterProp="label"
              disabled={isEdit}
              options={personnel}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CollocatedModal;

export const traverseDataNodes = (dataNodes: DataNode[], callback: (data: DataNode, childKeys: string[]) => void) => {
  function processNode(node: DataNode) {
    const children = node ? node.children : dataNodes;

    if (node) {
      const _childKeys: string[] = [];
      const dig = (dataNodes: DataNode[]) => {
        (dataNodes ?? []).forEach(item => {
          if (!item.disabled) {
            _childKeys.push(item.key as string);
          }
          dig(item.children ?? []);
        });
      };
      dig(node.children ?? []);
      callback(node, _childKeys);
    }

    if (children) {
      children.forEach(subNode => {
        processNode(subNode);
      });
    }
  }

  processNode(null as unknown as DataNode);
};

// 查找treeData中对应value的title（递归处理嵌套结构）
export const getTitleByValue = (data: any[], value: string): string => {
  return '';
};
